import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Button,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { createPopPlan } from "../../Services/Brokerage";
import { errorTost, successTost } from "../../Services/Toast";
const steps = [
  "Plan Details",
  "POP Validity",
  "Scope Type",
  "Scope Selection",
  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const StatBox = ({ label, value, highlight }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: highlight ? "#ECFDF3" : "#F4F7FA",
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography fontWeight={600}>{value}</Typography>
  </Box>
);

const CreatePopPlanStep10 = ({ data, setFormData, onBack }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [localFile, setLocalFile] = React.useState(data.file || null);
  const [fileError, setFileError] = React.useState(false);
  const handleActivatePop = async () => {
    if (!localFile) {
      setFileError(true);
      return;
    }

    try {
      setLoading(true);

      await createPopPlan({
        ...data,
        file: localFile,
      });

      navigate("/home/BenefitPlan", { state: { timestamp: Date.now() } });
      successTost("POP Plan created successfully!");
    } catch (err) {
      console.error("POP ERROR:", err);
      errorTost("Something failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box p={1}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{ mb: 2, cursor: "pointer", color: "text.secondary" }}
        onClick={onBack}
      >
        ← Back to POPs
      </Typography>

      {/* Stepper */}
      <Stepper activeStep={8} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  width: 36,
                  height: 36,
                  fontSize: "1rem",
                  "&.Mui-completed": { color: "#2563EB" },
                  "&.Mui-active": {
                    color: "#2563EB",
                    transform: "scale(1.15)",
                  },
                },
              }}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: "0.85rem",
                  mt: 1,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Typography variant="subtitle2" color="text.secondary" mb={2}>
        Step 9 of 9: <strong>Review</strong> — Preview & save
      </Typography>

      {/* Main Card */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          {/* Title */}
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <CheckCircleOutlineIcon color="success" />
            <Typography fontWeight={600}>Review POP Configuration</Typography>
          </Box>

          {/* Summary Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 3,
            }}
          >
            <StatBox label="PLAN NAME" value={data.planName || "-"} highlight />

            <StatBox
              label="STATUS"
              value={
                <Chip
                  label="Active"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              }
            />

            <StatBox
              label="CHANNEL PARTNERS"
              value={
                data.channelPartnerType === "all"
                  ? "All"
                  : `${data.selectedChannelPartners?.length || 0} selected`
              }
            />

            <StatBox
              label="SLABS"
              value={`${data.rangeRules?.length || 0} rules defined`}
            />

            <StatBox label="SCOPE TYPE" value="Wing Based" />

            <StatBox
              label="AOP CONFLICT"
              value={
                data.conflictStrategy === "allowBoth"
                  ? "POP + AOP"
                  : "POP Exclusive"
              }
            />

            <StatBox label="PROJECT" value={data.projectName || "-"} />

            <StatBox
              label="ELIGIBILITY CONSTRAINTS"
              value={
                data.eligibility?.siteVisitEnabled
                  ? `Site Visits ≥ ${data.eligibility.siteVisitMin}`
                  : data.eligibility?.fosEnabled
                    ? `FOS ≥ ${data.eligibility.fosMin}`
                    : "None"
              }
            />

            <StatBox
              label="VALIDITY"
              value={`${data.startDate} to ${data.endDate}`}
            />

            {localFile && (
              <StatBox
                label="DOCUMENT"
                value={
                  <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionOutlinedIcon fontSize="small" />
                    Selected: {localFile.name}
                  </Box>
                }
              />
            )}

            <StatBox
              label="SIGN-OFF"
              value={data.requireSignOff ? "Required" : "Not Required"}
            />
          </Box>

          <Box mt={2}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<DescriptionOutlinedIcon />}
            >
              Upload Document
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  setLocalFile(file);
                  setFileError(false);

                  setFormData((prev) => ({
                    ...prev,
                    file: file,
                  }));
                }}
              />
            </Button>

            {localFile && (
              <Typography variant="caption" display="block" mt={1}>
                Selected: {localFile.name}
              </Typography>
            )}

            {fileError && !localFile && (
              <Typography
                color="error"
                variant="caption"
                display="block"
                mt={1}
              >
                Document upload is required.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button variant="outlined" onClick={onBack}>
          ← Back
        </Button>

        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleActivatePop}
          disabled={loading || !localFile}
        >
          {loading ? "Activating..." : "Activate POP"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep10;
